import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Collapse,
  Typography
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Notifications as NotificationIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Importar tipos de mensagem do formatadores.js
import { MESSAGE_TYPES } from '../utils/formatadores';
// ✅ NOVO: Importar configurações de ambiente
import env from '../config/env';

// Re-exportar para compatibilidade
export { MESSAGE_TYPES };

// Configurações de cada tipo de mensagem
const MESSAGE_CONFIG = {
  [MESSAGE_TYPES.INFO]: {
    icon: InfoIcon,
    color: '#2196F3',
    bgColor: '#E3F2FD',
    title: 'Informação'
  },
  [MESSAGE_TYPES.WARNING]: {
    icon: WarningIcon,
    color: '#FF9800',
    bgColor: '#FFF3E0',
    title: 'Aviso'
  },
  [MESSAGE_TYPES.ERROR]: {
    icon: ErrorIcon,
    color: '#F44336',
    bgColor: '#FFEBEE',
    title: 'Erro'
  },
  [MESSAGE_TYPES.SUCCESS]: {
    icon: SuccessIcon,
    color: '#4CAF50',
    bgColor: '#E8F5E8',
    title: 'Sucesso'
  },
  [MESSAGE_TYPES.NOTIFICATION]: {
    icon: NotificationIcon,
    color: '#9E9E9E',
    bgColor: '#F5F5F5',
    title: 'Notificação'
  }
};

// Hook para gerenciar mensagens do sistema
export const useSystemMessages = () => {
  const [messages, setMessages] = useState([]);

  const addMessage = (type, message, title = null, autoClose = true, duration = 5000) => {
    const id = Date.now() + Math.random();
    const newMessage = {
      id,
      type,
      message,
      title: title || MESSAGE_CONFIG[type]?.title,
      autoClose,
      duration,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);

    // Auto-close se configurado
    if (autoClose) {
      setTimeout(() => {
        removeMessage(id);
      }, duration);
    }

    return id;
  };

  const removeMessage = (id) => {
    setMessages(prev => prev.filter(msg => msg.id !== id));
  };

  const clearAllMessages = () => {
    setMessages([]);
  };

  const addInfo = (message, title = null, autoClose = true, duration = env.NOTIFICATION_DURATION || 5000) => {
    return addMessage(MESSAGE_TYPES.INFO, message, title, autoClose, duration);
  };

  const addWarning = (message, title = null, autoClose = true, duration = env.NOTIFICATION_DURATION || 7000) => {
    return addMessage(MESSAGE_TYPES.WARNING, message, title, autoClose, duration);
  };

  const addError = (message, title = null, autoClose = false, duration = env.NOTIFICATION_DURATION || 10000) => {
    return addMessage(MESSAGE_TYPES.ERROR, message, title, autoClose, duration);
  };

  const addSuccess = (message, title = null, autoClose = true, duration = env.NOTIFICATION_DURATION || 4000) => {
    return addMessage(MESSAGE_TYPES.SUCCESS, message, title, autoClose, duration);
  };

  const addNotification = (message, title = null, autoClose = true, duration = env.NOTIFICATION_DURATION || 6000) => {
    return addMessage(MESSAGE_TYPES.NOTIFICATION, message, title, autoClose, duration);
  };

  return {
    messages,
    addMessage,
    removeMessage,
    clearAllMessages,
    addInfo,
    addWarning,
    addError,
    addSuccess,
    addNotification
  };
};

// Componente principal de mensagens do sistema
const SystemMessages = ({ messages, onRemoveMessage, maxMessages = 3 }) => {
  // Mostrar apenas as últimas mensagens
  const visibleMessages = messages.slice(-maxMessages);

  if (visibleMessages.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        p: 2
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: '400px',
          marginLeft: 'auto',
          pointerEvents: 'auto'
        }}
      >
        {visibleMessages.map((msg) => {
          const config = MESSAGE_CONFIG[msg.type];
          const IconComponent = config?.icon || InfoIcon;

          return (
            <Collapse key={msg.id} in={true} timeout={300}>
              <Box
                sx={{
                  backgroundColor: config?.bgColor || '#F5F5F5',
                  border: `1px solid ${config?.color || '#9E9E9E'}`,
                  borderRadius: 1,
                  p: 1.5,
                  boxShadow: 2,
                  position: 'relative',
                  minHeight: '48px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1
                }}
              >
                {/* Ícone */}
                <IconComponent
                  sx={{
                    color: config?.color || '#9E9E9E',
                    fontSize: '20px',
                    mt: 0.2,
                    flexShrink: 0
                  }}
                />

                {/* Conteúdo */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Título */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: config?.color || '#9E9E9E',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}
                    >
                      {msg.title}
                    </Typography>
                    
                    {/* Timestamp */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.75rem'
                      }}
                    >
                      {msg.timestamp.toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>

                  {/* Mensagem */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      lineHeight: 1.4,
                      wordBreak: 'break-word'
                    }}
                  >
                    {msg.message}
                  </Typography>
                </Box>

                {/* Botão fechar */}
                <IconButton
                  size="small"
                  onClick={() => onRemoveMessage(msg.id)}
                  sx={{
                    color: config?.color || '#9E9E9E',
                    p: 0.5,
                    ml: 1,
                    flexShrink: 0
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Collapse>
          );
        })}

        {/* Indicador de múltiplas mensagens */}
        {messages.length > maxMessages && (
          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              borderRadius: 1,
              p: 1,
              textAlign: 'center',
              fontSize: '0.75rem'
            }}
          >
            +{messages.length - maxMessages} mensagens anteriores
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SystemMessages; 